import clsx from "clsx";
import { FunctionComponent } from "react";

import HamburgerIcon from "@ui/components/icon/HamburgerIcon";
import NotificationsButton from "@ui/components/navigation/Notifications/NotificationsButton";
import SELECTORS from "@ui/config/class-selectors.config";
import { useNavigationContext } from "@ui/context/navigation-context";
import { withGracefulErrorBoundary } from "@ui/errors/GracefulErrorBoundary";
import { useAnalytics } from "@voxmedia/duet-context/context/analytics-context";

import * as styles from "./MinimalNav.css";

interface Props {
  Logo: FunctionComponent<{ className?: string }>;
  /**
   * Logo rendered in the notifications popover/drawer header. When present
   * alongside `showNotificationsButton`, the bell renders to the left of the
   * hamburger.
   */
  NotificationsLogo?: FunctionComponent<{ className?: string }>;
  headline?: string;
  isBranded?: boolean;
  /**
   * Brand-config flag (currently Verge-only) that opts a network into showing
   * the notifications bell inside the minimal-nav layout.
   */
  showNotificationsButton?: boolean;
}

const MinimalNav: FunctionComponent<Props> = ({
  Logo,
  NotificationsLogo,
  headline,
  isBranded = false,
  showNotificationsButton = false,
}) => {
  const { openDrawer, drawerId } = useNavigationContext();
  const analytics = useAnalytics();
  const handleMoreBtnClick = () => {
    openDrawer();
    analytics?.emit({
      event: "navigation_click",
      navigation_click: { name: "minimal nav more button" },
    });
  };

  const variant = isBranded ? "branded" : "normal";

  return (
    <nav
      aria-label="Navigation Bar"
      className={clsx(SELECTORS.MinimalNav, styles.container[variant])}
    >
      <div className={styles.innerContainer}>
        <a
          href="/"
          aria-label="Click to visit the homepage"
          tabIndex={-1}
          className={styles.logoContainer}
        >
          <Logo className={clsx(styles.logo[variant])} />
        </a>

        {headline && <div className={styles.headline}>{headline}</div>}
        <div className={styles.rightMenu}>
          {showNotificationsButton && NotificationsLogo && (
            <NotificationsButton Logo={NotificationsLogo} popoverId="minimal" />
          )}
          <button
            onClick={handleMoreBtnClick}
            aria-controls={drawerId}
            aria-label="Open Drawer"
          >
            <HamburgerIcon className={clsx(styles.moreIcon[variant])} />
          </button>
        </div>
      </div>
    </nav>
  );
};

export default withGracefulErrorBoundary(MinimalNav, {
  context: "MinimalNavErrorBoundary",
});
